/* Popover for editing a feed item note. Wraps a trigger element (the note button). */

import { useState, useEffect, type ReactNode } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

interface NoteEditPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialNote: string;
  onSave: (note: string) => void;
  children: ReactNode;
}

export function NoteEditPopover({ open, onOpenChange, initialNote, onSave, children }: NoteEditPopoverProps) {
  const [draft, setDraft] = useState(initialNote);

  useEffect(() => {
    if (open) setDraft(initialNote);
  }, [open, initialNote]);

  function handleSave() {
    onSave(draft);
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        <textarea
          autoFocus
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSave();
            }
          }}
          placeholder="Add a note..."
          className="bg-transparent border border-input rounded-md text-sm p-2 resize-none w-full focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">
            {navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}+Enter to save
          </span>
          <Button size="sm" className="h-7 text-xs" onClick={handleSave}>Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
